import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useTranslation from "@/hooks/useTranslation";
import logo from "@/../public/images/navbar-logo.png";

const Navbar = () => {
  const { t, language } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get language-specific paths
  const getLocalizedPath = (path) => {
    if (language === 'es') {
      // For Spanish, use localized paths
      if (path === '/') return '/es';
      if (path === '/services') return '/es/servicios';
      if (path === '/contact') return '/es/contacto';
      if (path === '/privacy') return '/es/privacidad';
      if (path === '/terms') return '/es/terminos';
    }
    return path; // Default to English paths
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <nav className="bg-white shadow-lg" role="navigation" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex-shrink-0 flex items-center">
            <Link to={getLocalizedPath('/')} aria-label="Home">
              <img
                src={logo}
                alt="NuWav Laboratories Logo"
                className="h-12"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden xs:flex items-center space-x-8">
            <NavLink to={getLocalizedPath('/')}>{t('home')}</NavLink>
            <NavLink to={getLocalizedPath('/services')}>{t('ourServices')}</NavLink>
            <NavLink to={getLocalizedPath('/contact')}>{t('contact')}</NavLink>
          </div>
          
          {/* Mobile menu button */}
          <div className="xs:hidden flex items-center">
            <button 
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-[#40E0D0] focus:outline-none"
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle menu"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                ) : (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 6h16M4 12h16M4 18h16" 
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="xs:hidden bg-white border-t border-gray-200"
          >
            <div className="flex flex-col py-2 space-y-2 px-4">
              <MobileNavLink to={getLocalizedPath('/')} onClick={toggleMobileMenu}>
                {t('home')}
              </MobileNavLink>
              <MobileNavLink to={getLocalizedPath('/services')} onClick={toggleMobileMenu}>
                {t('ourServices')}
              </MobileNavLink>
              <MobileNavLink to={getLocalizedPath('/contact')} onClick={toggleMobileMenu}>
                {t('contact')}
              </MobileNavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const NavLink = ({ to, children }) => (
  <Link to={to}>
    <motion.span
      className="text-gray-700 hover:text-[#40E0D0] font-medium transition-colors"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      role="menuitem"
    >
      {children}
    </motion.span>
  </Link>
);

const MobileNavLink = ({ to, children, onClick }) => (
  <Link to={to} onClick={onClick}>
    <motion.div
      className="text-gray-700 hover:text-[#40E0D0] font-medium py-2 transition-colors"
      whileTap={{ scale: 0.95 }}
      role="menuitem"
    >
      {children}
    </motion.div>
  </Link>
);

export default Navbar;
